import React, { useState } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Button,
  FormControlLabel,
  RadioGroup,
  Radio,
  Grid,
  Typography,
  Container,
  Paper,
  Box,
  Autocomplete,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChatIcon from "@mui/icons-material/Chat";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import ChatWindow from "./Helpers/ChatWindow";
import { useForm, Controller } from "react-hook-form";
import Navbar from "../../../globals/components/Navbar";
import InputMask from "react-input-mask";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useCallback, useEffect, useRef } from "react";
import { apiClient } from "../../../globals/utils/api";
import { loadGoogleMaps } from "../../../globals/utils/googleMapsLoader";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { updateBondNumber } from "../../../globals/utils/commons";
import { removeCountryCode } from "../../../globals/utils/commons";
import { BondNumberSelect } from "./Helpers/BondNumberSelect";
import { increment } from "../../../store/slices/countSlice";
import { useDispatch } from "react-redux";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { is_prod } from "../../../globals/utils/constants";
import { extractErrorMessage } from "../../../globals/utils/error-handler";
import { buildFullName } from "../../../utils/nameUtils";

// Google Autocomplete Component
const GoogleAutoComplete = ({ placeholder, callback }) => {
  const autoCompleteRef = useRef();
  const inputRef = useRef();
  const [loaded, setLoaded] = useState(false);

  const loadGoogleMapsScript = useCallback(() => {
    loadGoogleMaps()
      .then(() => setLoaded(true))
      .catch((e) => console.error(e));
  }, []);

  useEffect(() => {
    loadGoogleMapsScript();
  }, [loadGoogleMapsScript]);

  useEffect(() => {
    if (loaded && window.google) {
      autoCompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          fields: ["address_components", "geometry"],
          types: ["address"], // better for full addresses
        }
      );

      autoCompleteRef.current.addListener("place_changed", () => {
        const place = autoCompleteRef.current.getPlace();

        if (!place || !place.address_components) {
          console.error("No valid place returned or missing address components.");
          return;
        }

        let address = "",
          city = "",
          state = "",
          zipCode = "";

        place.address_components.forEach((component) => {
          const types = component.types;

          if (types.includes("street_number")) {
            address = `${component.long_name} ${address}`;
          }
          if (types.includes("route")) {
            address += component.long_name;
          }

          // City fallbacks
          if (types.includes("locality")) {
            city = component.long_name;
          }
          if (!city && (types.includes("sublocality") || types.includes("sublocality_level_1"))) {
            city = component.long_name;
          }
          if (!city && types.includes("administrative_area_level_2")) {
            city = component.long_name;
          }

          if (types.includes("administrative_area_level_1")) {
            state = component.long_name; // ✅ full state name
          }

          if (types.includes("postal_code")) {
            zipCode = component.long_name;
          }
        });

        // Fallback: geocoder if city/state missing
        if ((!city || !state) && place.geometry?.location) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: place.geometry.location }, (results, status) => {
            if (status === "OK" && results[0]) {
              results[0].address_components.forEach((component) => {
                const types = component.types;
                if (!city && types.includes("locality")) {
                  city = component.long_name;
                }
                if (!state && types.includes("administrative_area_level_1")) {
                  state = component.long_name; // ✅ full name
                }
              });
              if (callback) callback(address, city, state, zipCode);
            }
          });
        } else {
          if (callback) callback(address, city, state, zipCode);
        }
      });
    }
  }, [loaded, callback]);

  return (
    <TextField
      variant="outlined"
      fullWidth
      placeholder={placeholder}
      inputRef={inputRef}
      sx={{ marginBottom: "8px" }}
    />
  );
};

const VermostBlanketBondRetrieveForm = () => {
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm();
  const [data, setData] = useState(null);
  const [editableCreatedAt, setEditableCreatedAt] = useState("");
  const navigate = useNavigate();
  const [allCities, setAllCities] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [openChat, setOpenChat] = useState(false);
  const [chatUnreadTotal, setChatUnreadTotal] = useState(0);
  const [billingMethod, setBillingMethod] = useState("");
  const [isIndemnityReceived, setIsIndemnityReceived] = useState("");
  const [state, setState] = useState(null);
  const [allCounties, setAllCounties] = useState([]);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const userdetails = useSelector((state) => state.auth.user);
  const { role } = useSelector((state) => state.auth);
  const disabled = role !== "super_user" && role !== "internal_user";
  const pii_access = userdetails?.is_pii_user;
  const [loaded, setLoaded] = useState(false);
  const { bond_id } = useParams();
  const [updateLoading, setUpdateLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [isUnderwritingLoading, setIsUnderwritingLoading] = useState(false);
  const [bondApproved, setBondApproved] = useState(false);
  const [pgId, setPgId] = useState(null);
  const [pgData, setPgData] = useState(null);
  const [isApproveLoading, setIsApproveLoading] = useState(false);
  const [isRejectLoading, setIsRejectLoading] = useState(false);
  const [isEsignatureLoading, setIsEsignatureLoading] = useState(false);
  const username = useSelector((state) => state.auth?.user?.username) || "";
  const [obligees, setObligees] = useState([]);
  const [loadingObligees, setLoadingObligees] = useState(false);
  const [isCreditDialogOpen, setIsCreditDialogOpen] = useState(false);
  const [creditReportFile, setCreditReportFile] = useState(null);
  const [isCreditUploading, setIsCreditUploading] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectFcraFile, setRejectFcraFile] = useState(null);
  const [rejectFcraUploading, setRejectFcraUploading] = useState(false);
  const [openObligeeDialog, setOpenObligeeDialog] = useState(false);
  const [newObligee, setNewObligee] = useState({
    name: "",
    street: "",
    city: "",
    state: "",
    zip: "",
  });

  const [showSSN, setShowSSN] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  useEffect(() => {
    let isMounted = true;
    const fetchCounts = async () => {
      try {
        const counts = await apiClient(`/messages-recent-by-sender/${bond_id}`, "GET");
        if (!isMounted) return;
        const total = Object.entries(counts || {}).reduce((sum, [senderType, count]) => {
          return senderType === role ? sum : sum + (Number(count) || 0);
        }, 0);
        setChatUnreadTotal(total);
      } catch (e) {}
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [bond_id, role]);
  // State for underwriting dropdown
  const [underwritingAnchorEl, setUnderwritingAnchorEl] = React.useState(null);

  const [bondNum, setBondNum] = useState({
    bond_number: null, // Default state for bond number
  });
  const dispatch = useDispatch();

  // Callback function to update data
  const updateBondNum = (updatedData) => {
    setBondNum(updatedData);
  };

  // Fetch cities and states for dropdowns
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const cities = await apiClient("/fields/city/", "GET");
        const states = await apiClient("/fields/states/", "GET");
        setAllCities(cities);
        setAllStates(states);
      } catch (error) {
        console.error("Error loading cities or states:", error);
      }
    };
    loadDropdownData();

    const savedState = localStorage.getItem("selectedState");

    if (savedState) {
      const parsedState = JSON.parse(savedState);
      setState(parsedState);
    }
  }, []);

  // Fetch counties when state changes
  useEffect(() => {
    const fetchCounties = async () => {
      if (state && state.id) {
        setLoadingCounties(true); // Start loader
        try {
          const countyResponse = await apiClient(
            `/counties-by-state/${state.id}/`,
            "GET"
          );
          const formattedCounties = countyResponse.map(
            (county) => county.bond_county
          );
          setAllCounties(formattedCounties);
        } catch (error) {
          console.error("Error fetching counties:", error);
        } finally {
          setLoadingCounties(false); // Stop loader
        }
      }
    };

    fetchCounties();
  }, [state]);

  useEffect(() => {
    setLoaded(false);
    const fetchBondData = async () => {
      try {
        const response = await apiClient(
          `/retrieve-blanket-bond/${bond_id}/`,
          "GET"
        );
        if (response) {
          const { bond, blanketbond } = response;
          setData(response);
          const createdAt = response?.created_at ?? bond?.created_at;
          setEditableCreatedAt(
            createdAt
              ? (() => {
                  const d = new Date(createdAt);
                  if (Number.isNaN(d.getTime())) return "";
                  const pad = (n) => String(n).padStart(2, "0");
                  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                })()
              : ""
          );
          setBillingMethod(bond.billing_method);
          setIsIndemnityReceived(
            blanketbond?.is_indemnity_received === null
              ? ""
              : blanketbond?.is_indemnity_received
              ? "Yes"
              : "No"
          );

          // Add obligee fetch
          const obligeesRes = await apiClient("/obligee-list-create/");
          setObligees(obligeesRes);

          // Prefill form fields
          setValue("firstName", blanketbond.first_name || "");
          setValue("middleName", blanketbond.middle_name || "");
          setValue("lastName", blanketbond.last_name || "");
          setValue("preferredName", blanketbond.preferred_name || "");
          setValue("dob", blanketbond.dob);
          setValue("doe", blanketbond.date_of_expiration);
          setValue("address", blanketbond.street_address_line_1 || "");
          setValue("city", blanketbond.city || "");
          setValue("state", blanketbond.state || "");
          setValue("zipCode", blanketbond.zip || "");
          setValue("cell_phone", blanketbond.cell_phone || "");
          setValue("mayWeText", blanketbond.should_we_text ? "Yes" : "No");
          setValue("email", blanketbond.email || "");
          setValue("is_esignatures", bond.is_esignatures ? "Yes" : "No");
          setValue("usCitizen", blanketbond.is_us_citizen ? "Yes" : "No");
          setValue("residencyStatus", blanketbond.residency_status || "");
          setValue("bankrupt", blanketbond.bankrupcy ? "Yes" : "No");
          setValue(
            "bankruptExplanation",
            blanketbond.bankrupcy_explanation || ""
          );
          setValue("lawsuits", blanketbond.judgements ? "Yes" : "No");
          setValue(
            "lawsuitsExplanation",
            blanketbond.judgements_explanation || ""
          );
          setValue("declinedSurety", blanketbond.is_decline ? "Yes" : "No");
          setValue("suretyExplanation", blanketbond.declined_explanation || "");
          setValue("replacingBond", blanketbond.is_replace ? "Yes" : "No");
          setValue(
            "bondReplacementReason",
            blanketbond.surety_name_reason || ""
          );
          setValue(
            "authorizationAgreement",
            blanketbond.authorization_underwriting
          );
          setValue("ssn", blanketbond.ssn);
          setValue(
            "cancellationDocument",
            blanketbond.cancellation_document || ""
          );
          setValue(
            "cancellationDocumentLink",
            blanketbond.cancellation_document_link || ""
          );
          setValue("creditDocument", blanketbond.credit_report_document || "");
          setValue(
            "creditDocumentLink",
            blanketbond.credit_report_document_link || ""
          );
          setValue("fcraDocument", blanketbond.fcra_document || "");
          setValue("fcraDocumentLink", blanketbond.fcra_document_link || "");
          setValue(
            "verificationCertDocument",
            blanketbond.verification_certificate_document || ""
          );
          setValue(
            "verificationCertDocumentLink",
            blanketbond.verification_certificate_document_link || ""
          );
          setValue(
            "verificationPoaDocument",
            blanketbond.verification_poa_document || ""
          );
          setValue(
            "verificationPoaDocumentLink",
            blanketbond.verification_poa_document_link || ""
          );
          setValue("bondTerm", bond.bond_sub_category || "");
          setValue("is_underwriting", bond.is_underwriting || false);
          setValue(
            "underwriting_status",
            response.underwriting_status || "Not Required"
          );
          setValue(
            "underwriting_reason",
            response.underwriting_reason || "N/A"
          );
          setValue("overrided_by", response.overrided_by || "");
          setValue("county", bond.county || "");
          setValue("number", bond.bond_number);
          setValue("obligeeId", bond.obligee || null);
          setValue(
            "bondAppFileName",
            bond.first_document_name || "No file uploaded"
          );
          setValue("bondAppLink", bond.first_document_url || "");
          setValue(
            "bondFileName",
            bond.second_document_name || "No file uploaded"
          );
          setValue("bondLink", bond.second_document_url || "");
          setValue(
            "indemnityFileName",
            bond.third_document_name || "No file uploaded"
          );
          setValue("indemnityLink", bond.third_document_url || "");
          setPgId(blanketbond.pg_id);
          setLoaded(true);
          setBondNum({ bond_number: bond.bond_number });
        }
      } catch (error) {
        console.error("Error fetching bond data:", error);
        toast.error("Failed to load bond data.");
        setLoaded(true);
      }
    };

    fetchBondData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bond_id, setValue]);

  const handleGoogleCallback = (address, city, state, zipCode) => {
    setValue("address", address);
    setValue("city", city);
    setValue("state", state);
    setValue("zipCode", zipCode);
  };

  const handleObligeeGoogleCallback = (address, city, state, zipCode) => {
    setNewObligee((prev) => ({
      ...prev,
      street: address,
      city:
        allCities.find((c) => c.toLowerCase() === city.toLowerCase()) || city,
      state:
        allStates.find((s) => s.toLowerCase() === state.toLowerCase()) || state,
      zip: zipCode,
    }));
  };

  const handleAddObligee = async () => {
    try {
      setLoadingObligees(true);
      const response = await apiClient("/obligee-list-create/", "POST", {
        obligee_name: newObligee.name,
        obligee_address: newObligee.street,
        obligee_city: newObligee.city,
        obligee_state: newObligee.state,
        obligee_zip: newObligee.zip,
      });

      setObligees((prev) => [...prev, response]);
      setValue("obligeeId", response.obligee_id);
      setOpenObligeeDialog(false);
      setNewObligee({ name: "", street: "", city: "", state: "", zip: "" });
      toast.success("Obligee added successfully!");
    } catch (error) {
      console.error("Error adding obligee:", error);
      toast.error("Failed to add obligee");
    } finally {
      setLoadingObligees(false);
    }
  };

  const calculatePremium = (bondTerm) => {
    const t = (bondTerm || "").trim();
    switch (t) {
      case "1 year Bond":
      case "1 Year Bond":
        return 175.0;
      case "2 year Bond":
      case "2 Year Bond":
        return 306.0;
      case "3 year Bond":
      case "3 Year Bond":
        return 437.0;
      default:
        return 175.0;
    }
  };

  useEffect(() => {
    async function fetchGuardianData() {
      if (pgId !== null) {
        const guardianData = await apiClient(
          `/professional-guardian-user/${pgId}/`
        );
        setPgData(guardianData);
      }
    }
    fetchGuardianData();
  }, [pgId]);

  const formatDate = (date) => {
    const d = new Date(date);
    const month = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleApprove = async (data, option) => {
    if (option.approval === "approval") {
      setApproveLoading(true);
    } else if (option.approval === "verification") {
      setVerificationLoading(true);
    }

    let customerNumberAms = null;

    // If AMS customer number is missing, create it and await the result
    if (pgId !== null && (pgData == null || pgData.ams_customer_number == null)) {
      const customerData = {
        firm_name: `${data.firstName} ${data.lastName}`,
        address: data.address,
        city: data.city,
        state: data.state,
        zip: data.zipCode,
        email: data.email,
        user_type: "pg",
        pg_id: pgId,
      };

      try {
        const response = await apiClient(
          "/customer-creation-ams360",
          "POST",
          customerData
        );
        const customerNumber =
          response?.data?.customer_number ?? response?.customer_number;
        if (customerNumber) {
          console.log("AMS360 customer_number:", customerNumber);
          customerNumberAms = customerNumber;
          setPgData((prevData) => ({
            ...(prevData || {}),
            ams_customer_number: customerNumber,
          }));
        }
      } catch (error) {
        console.error("Error creating customer in AMS 360:", error);
      }
    }
    

    const currentDate = formatDate(new Date());
    let bondNumber = data.number?.bond_number;

    const oldBondNumber = data.number?.bondno_id || null;
    const newBondNumber = bondNum.bond_number?.bondno_id || null;

    if ((data.number === null && bondApproved === false) || (oldBondNumber !== newBondNumber)) {
      // const response = await apiClient(`/first-unused-bond/`, "GET");
      bondNumber = bondNum.bond_number.bond_number;
      await updateBondNumber(
        bondNum.bond_number.bondno_id,
        bond_id,
        data.firstName + " " + data.lastName + "(Blanket Bond)"
      );
      setBondApproved(true);
    }

    const bondTermMatch = data.bondTerm?.match(/^(\d+)\s*[Yy]ear\s*[Bb]ond$/);
    const bondYears = bondTermMatch ? parseInt(bondTermMatch[1], 10) : 0;

    let baseDate = data.doe ? new Date(data.doe) : new Date();

    if (isNaN(baseDate.getTime())) {
      baseDate = new Date();
    }

    baseDate.setFullYear(baseDate.getFullYear() + bondYears);

    const expirationDate = `${(baseDate.getMonth() + 1)
      .toString()
      .padStart(2, "0")}/${baseDate
      .getDate()
      .toString()
      .padStart(2, "0")}/${baseDate.getFullYear()}`;

    const issueDatePayload = option.effective_date != null && option.effective_date !== "" ? option.effective_date : (data.doe ? formatDate(data.doe) : currentDate);
    const expirationDatePayload = option.expiration_date != null && option.expiration_date !== "" ? option.expiration_date : expirationDate;

    const rawCustomerNo = pgData?.ams_customer_number ?? customerNumberAms;
    const customerNo =
      rawCustomerNo != null && rawCustomerNo !== ""
        ? parseInt(String(rawCustomerNo), 10)
        : null;

    if (option.ams_flow && (!customerNo || Number.isNaN(customerNo))) {
      setApproveLoading(false);
      setVerificationLoading(false);
      toast.error(
        "AMS customer number is missing or invalid. Cannot create policy in AMS360."
      );
      return;
    }

    try {
      const payload = {
        is_prod: is_prod,
        bond_id: parseInt(bond_id),
        ams_flow: option.ams_flow,
        general_indemnity: false,
        send_to_vermost: true,
        patch_type: "approve",
        approver: "dvermost@vermost.com",
        bond_type: "Blanket",
        bond_sub_category: data.bondTerm,
        bond_description: "Blanket Bond - " + data.bondTerm,
        bond_amount: "$50,000",
        bond_number: bondNumber,
        input_by: userdetails.first_name + " " + userdetails.last_name,
        bond_requestor_fullname:
          userdetails.first_name + " " + userdetails.last_name,
        bond_requestor_email: data.email,
        customer_no: customerNo,
        premium: calculatePremium(data.bondTerm),
        county: data.county,
        name: buildFullName(data.firstName, data.middleName, data.lastName),
        address: `${data.address}, ${data.city}, ${data.state}, ${data.zipCode}`,
        phone: removeCountryCode(data.cell_phone),
        signed_date: currentDate,
        issue_date: issueDatePayload,
        expiration_date: expirationDatePayload,
        is_us_citizen: data.usCitizen,
        residency: data.usCitizen === "Yes" ? "USA" : "",
        is_bankrupt: data.bankrupt,
        is_bankrupt_explanation: data.bankruptExplanation,
        is_lawsuit: data.lawsuits,
        is_lawsuit_explanation: data.lawsuitsExplanation,
        is_declined: data.declinedSurety,
        is_declined_explanation: data.suretyExplanation,
        prior_surety: data.declinedSurety,
        prior_surety_explanation: data.suretyExplanation,
        is_successor: data.replacingBond,
        is_successor_explanation: data.bondReplacementReason,
        type_bond_permit: "Florida Professional Guardian Blanket Bond",
        obligee: "Governor of the State of Florida",
        estate_of: buildFullName(data.firstName, data.middleName, data.lastName),
        estate_of_first_name: data.firstName,
        estate_of_middle_name: data.middleName,
        estate_of_last_name: data.lastName,
        estate_of_suffix: "",
        is_verification_certificate:
          option.approval === "verification" ? true : false,
        principal1: {
          name: buildFullName(data.firstName, data.middleName, data.lastName),
          street: data.address,
          city: data.city,
          state: data.state,
          zip: data.zipCode,
          us_citizen: data.usCitizen,
          citizenship: data.usCitizen === "Yes" ? "USA" : "",
        },
        create_new_customer:
          pgData !== null && pgData.ams_customer_number === null,
        customer_info: {
          type: "Customer",
          names: "Business",
          firm_name: buildFullName(data.firstName, data.middleName, data.lastName),
          address: data.address,
          city: data.city,
          state: data.state,
          zip: data.zipCode,
          executive: "Darren Vermost",
          representative: "Darren Vermost",
          department: "Bonds",
          phone_number: removeCountryCode(data.cell_phone) || "",
          email: data.email || "",
        },
        billing_method: option.billing_method,
      };

      let docModule = "bond_blanket_word";
      if (option.approval === "verification"){
        docModule = "vermost_rider";
      }

      const dataForPost = {
        module: docModule,
        process: "main",
        email_list: ["bonds@vermost.com"],
        payload: payload,
      };
      await apiClient("/approve-blanket-bond/", "POST", dataForPost);
      const payload_update_status = {
        bond: {
          status: "Approved",
          updated_at: new Date().toISOString(),
          approved_at: new Date().toISOString(),
          billing_method: option.billing_method,
          liberty_api_status: option.approval === "verification" ? "approved" : "pending",
        },
        blanketbond: {},
      };
      await apiClient(
        `/update-blanket-bond/${bond_id}/`,
        "PATCH",
        payload_update_status
      );
      setApproveLoading(false);
      setVerificationLoading(false);
      if (option.approval === "approval") {
        toast.success("Bond Sent for Approval Successfully");
      } else if (option.approval === "verification") {
        toast.success("Bond Sent for Renewal Successfully");
      }
      dispatch(increment());
      // setTimeout(function () {
      //   window.close();
      // }, 2000);
    } catch (error) {
      setApproveLoading(false);
      const message = extractErrorMessage(error);
      toast.error(`Bond approval failed. ${message}`);
      console.error("Error approving blanket bond:", error);
    }
  };

  const uploadToADLS = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("file_name", file.name);
    const response = await apiClient("/bond-file-upload/", "POST", formData);
    if (!response.blob_link) throw new Error("Blob link not returned from the server.");
    return response.blob_link;
  };

  const handleReject = async (dataOrFcraOverride) => {
    try {
      setRejectLoading(true);
      const blanketbondPayload = (dataOrFcraOverride && typeof dataOrFcraOverride === "object" && (dataOrFcraOverride.fcra_document || dataOrFcraOverride.fcra_document_link))
        ? { fcra_document: dataOrFcraOverride.fcra_document || "", fcra_document_link: dataOrFcraOverride.fcra_document_link || "" }
        : {};
      const payload_update_status = {
        bond: {
          status: "Rejected",
          updated_at: new Date().toISOString(),
        },
        blanketbond: blanketbondPayload,
      };
      await apiClient(
        `/update-blanket-bond/${bond_id}/`,
        "PATCH",
        payload_update_status
      );
      setRejectLoading(false);
      setRejectDialogOpen(false);
      setRejectFcraFile(null);
      toast.success("Bond Rejected Successfully");
      dispatch(increment());
      setTimeout(function () {
        window.close();
      }, 2000);
    } catch (error) {
      setRejectLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setUpdateLoading(true);
    try {
      const payload = {
        bond: {
          bond_carrier: is_prod ? 7 : 4,
          bond_type: 5,
          bond_sub_category: data.bondTerm,
          bond_amount: 50000.0,
          premium: calculatePremium(data.bondTerm),
          state: 6,
          county: data.county,
          is_esignatures: data.is_esignatures === "Yes",
          name: `${data.firstName} ${data.lastName}`,
          obligee: data.obligeeId,
          updated_at: new Date().toISOString(),
          billing_method: billingMethod || "",
          ...(editableCreatedAt ? { created_at: new Date(editableCreatedAt).toISOString() } : {}),
        },
        blanketbond: {
          first_name: data.firstName,
          middle_name: data.middleName,
          last_name: data.lastName,
          preferred_name: data.preferredName,
          street_address_line_1: data.address,
          street_address_line_2: "",
          city: data.city,
          state: data.state,
          zip: data.zipCode,
          cell_phone: data.cell_phone,
          should_we_text: data.mayWeText === "Yes",
          email: data.email,
          dob: data.dob,
          date_of_expiration: data.doe,
          ssn: data.ssn,
          is_us_citizen: data.usCitizen === "Yes",
          residency_status: data.residencyStatus,
          bankrupcy: data.bankrupt === "Yes",
          bankrupcy_explanation: data.bankruptExplanation,
          judgements: data.lawsuits === "Yes",
          judgements_explanation: data.lawsuitsExplanation,
          is_decline: data.declinedSurety === "Yes",
          declined_explanation: data.suretyExplanation,
          is_replace: data.replacingBond === "Yes",
          surety_name_reason: data.bondReplacementReason,
          authorization_underwriting: data.authorizationAgreement,
          bond_term: data.bondTerm,
          updated_at: new Date().toISOString(),
          is_indemnity_received:
            isIndemnityReceived === "" ? null : isIndemnityReceived === "Yes",
          fcra_document: data.fcraDocument || "",
          fcra_document_link: data.fcraDocumentLink || "",
        },
      };

      const oldBondNumber = data.number?.bondno_id || null;
      const newBondNumber = bondNum.bond_number?.bondno_id || null;

      // Update must NOT assign a new bond number; it may only change if one already exists.
      if (oldBondNumber != null && newBondNumber != null && oldBondNumber !== newBondNumber) {
        await updateBondNumber(
          newBondNumber,
          bond_id,
          data.firstName + " " + data.lastName + "(Blanket Bond)"
        );
        setBondApproved(true);
      }

      await apiClient(
        `/update-blanket-bond/${bond_id}/`,
        "PATCH",
        payload
      );
      toast.success("Bond updated successfully!");
      setUpdateLoading(false);
      dispatch(increment());
    } catch (error) {
      setUpdateLoading(false);
      console.error("Error submitting form:", error);
      const message = extractErrorMessage(error);
      toast.error(`Failed to update bond. ${message}`); // Show error toast
    }
  };

  const handleUpdateUnderwriting = async (status) => {
    try {
      // Set the correct loading state based on the status
      if (status === "approved") {
        setIsApproveLoading(true); // Set approve loading state
      } else if (status === "rejected") {
        setIsRejectLoading(true); // Set reject loading state
      }
      setIsUnderwritingLoading(true); // Set general underwriting loading state

      // Call the API to update the underwriting status
      await apiClient(
        `/update_underwriting_status/${bond_id}/`,
        "PATCH",
        { status: status }
      );

      // Show success toast with dynamic status
      toast.success(`Underwriting ${status} successfully!`);
    } catch (error) {
      // Show error toast with dynamic status
      toast.error(`Failed to ${status} underwriting. Please try again.`);
    } finally {
      // Reset all loading states
      setIsApproveLoading(false);
      setIsRejectLoading(false);
      setIsUnderwritingLoading(false);
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // old : handleApproveOption(withInvoice)
  const handleApproveOption = (option) => {
    handleSubmit((formData) => handleApprove(formData, option))();
    handleMenuClose();
  };

  // Handlers for underwriting dropdown
  const handleUnderwritingMenuClick = (event) => {
    setUnderwritingAnchorEl(event.currentTarget);
  };

  const handleUnderwritingMenuClose = () => {
    setUnderwritingAnchorEl(null);
  };

  const [overrideReasonDialogOpen, setOverrideReasonDialogOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [manualUnderwritingDialogOpen, setManualUnderwritingDialogOpen] = useState(false);
  const [manualUnderwritingReason, setManualUnderwritingReason] = useState("");
  const handleOverrideUnderwritingClick = () => {
    handleUnderwritingMenuClose();
    setOverrideReason("");
    setOverrideReasonDialogOpen(true);
  };
  const handleManualUnderwritingClick = () => {
    handleUnderwritingMenuClose();
    setManualUnderwritingReason("");
    setManualUnderwritingDialogOpen(true);
  };
  const handleSubmitManualUnderwriting = async () => {
    const reason = (manualUnderwritingReason || "").trim();
    if (!reason) {
      toast.error("Please enter a reason for creating the underwriting entry.");
      return;
    }
    try {
      setIsUnderwritingLoading(true);
      if (!userdetails?.first_name || !userdetails?.last_name) {
        throw new Error("User information is incomplete");
      }
      await apiClient(
        `/trigger_underwriting/${bond_id}/`,
        "POST",
        {
          underwriting_reason: reason,
          created_by: `${userdetails.first_name} ${userdetails.last_name}`,
        },
        {},
        { "Content-Type": "application/json" }
      );
      toast.success("Underwriting entry created successfully.");
      setManualUnderwritingDialogOpen(false);
      setManualUnderwritingReason("");
    } catch (error) {
      console.error("Create underwriting entry error:", error);
      const errorMessage =
        error.detail ||
        error.message ||
        (error.response && error.response.data && error.response.data.detail) ||
        "Failed to create underwriting entry. Please try again.";
      toast.error(errorMessage);
      if (error.response?.status === 401) return;
    } finally {
      setIsUnderwritingLoading(false);
    }
  };
  const handleSubmitOverrideReason = async () => {
    const reason = (overrideReason || "").trim();
    if (!reason) {
      toast.error("Please enter a reason for overriding underwriting.");
      return;
    }
    try {
      setIsUnderwritingLoading(true);
      if (!userdetails?.first_name || !userdetails?.last_name) {
        throw new Error("User information is incomplete");
      }
      await apiClient(
        `/override_underwriting/${bond_id}/`,
        "POST",
        {
          overrided_by: `${userdetails.first_name} ${userdetails.last_name}`,
          override_reason: reason,
        },
        {},
        { "Content-Type": "application/json" }
      );
      toast.success("Underwriting overridden successfully");
      setOverrideReasonDialogOpen(false);
      setOverrideReason("");
    } catch (error) {
      console.error("Override underwriting error:", error);
      const errorMessage =
        error.detail ||
        error.message ||
        (error.response && error.response.data && error.response.data.detail) ||
        "Failed to override underwriting. Please try again.";
      toast.error(errorMessage);
      if (error.response?.status === 401) return;
    } finally {
      setIsUnderwritingLoading(false);
    }
  };

  const [renewalsAnchorEl, setRenewalsAnchorEl] = useState(null);
  const [verificationCertDialogOpen, setVerificationCertDialogOpen] = useState(false);
  const [verificationCertFormData, setVerificationCertFormData] = useState(null);
  const [verificationCertEffectiveDate, setVerificationCertEffectiveDate] = useState("");
  const [verificationCertExpirationDate, setVerificationCertExpirationDate] = useState("");
  const [verificationCertBondTerm, setVerificationCertBondTerm] = useState("1 Year Bond");

  const BOND_TERM_OPTIONS = ["1 Year Bond", "2 Year Bond", "3 Year Bond"];

  const handleVerificationCertificate = () => {
    handleSubmit((formData) => {
      const baseDate = formData.doe ? new Date(formData.doe) : new Date();
      const safeBase = isNaN(baseDate.getTime()) ? new Date() : baseDate;
      const bondTermMatch = (formData.bondTerm || "").match(/^(\d+)\s*[Yy]ear\s*[Bb]ond$/);
      const bondYears = bondTermMatch ? parseInt(bondTermMatch[1], 10) : 0;
      const expDate = new Date(safeBase);
      expDate.setFullYear(expDate.getFullYear() + bondYears);
      const toYMD = (d) => d.toISOString().slice(0, 10);
      setVerificationCertFormData(formData);
      setVerificationCertBondTerm(formData.bondTerm || "1 Year Bond");
      setVerificationCertEffectiveDate(formData.doe ? toYMD(new Date(formData.doe)) : toYMD(new Date()));
      setVerificationCertExpirationDate(toYMD(expDate));
      setVerificationCertDialogOpen(true);
    })();
    setRenewalsAnchorEl(null);
  };

  const ymdToMdy = (ymd) => {
    if (!ymd) return "";
    const parts = ymd.split("-");
    if (parts.length !== 3) return ymd;
    return `${parts[1]}/${parts[2]}/${parts[0]}`;
  };

  const handleVerificationCertConfirm = async () => {
    if (!verificationCertFormData) return;
    const effectiveMdy = ymdToMdy(verificationCertEffectiveDate);
    const expirationMdy = ymdToMdy(verificationCertExpirationDate);
    setVerificationCertDialogOpen(false);

    const bondTermChanged = verificationCertBondTerm !== (verificationCertFormData.bondTerm || "");
    if (bondTermChanged) {
      try {
        setVerificationLoading(true);
        await apiClient(
          `/update-blanket-bond/${bond_id}/`,
          "PATCH",
          {
            bond: {
              bond_sub_category: verificationCertBondTerm,
              premium: calculatePremium(verificationCertBondTerm),
              updated_at: new Date().toISOString(),
            },
            blanketbond: {
              bond_term: verificationCertBondTerm,
              updated_at: new Date().toISOString(),
            },
          }
        );
        toast.success("Bond term updated.");
      } catch (error) {
        setVerificationLoading(false);
        const message = extractErrorMessage(error);
        toast.error(`Failed to update bond term. ${message}`);
        return;
      }
    }

    const formDataWithTerm = { ...verificationCertFormData, bondTerm: verificationCertBondTerm };
    handleApprove(formDataWithTerm, {
      approval: "verification",
      billing_method: billingMethod || "Agency Bill",
      ams_flow: false,
      effective_date: effectiveMdy,
      expiration_date: expirationMdy,
    });
    setVerificationCertFormData(null);
  };

  const handleSignIndemnity = async () => {
    setIsEsignatureLoading(true);
    try {
      await apiClient(
        `/bond-signature/${bond_id}/`,
        "POST",
      );
      toast.success("E-signature request sent successfully");
    } catch (error) {
      toast.error("Failed to send e-signature request.");
      console.error(error);
    } finally {
      setIsEsignatureLoading(false);
    }
  };

  const runCreditReport = async (bondId, file) => {
    const formData = new FormData();
    formData.append("credit_report_document", file);

    return await apiClient(
      `/run-credit-report/?bond_id=${bondId}`,
      "POST",
      formData,
      {},
      {
        "Content-Type": "multipart/form-data",
      }
    );
  };



  if (!loaded) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const BasicInformation = ({ isDisabled }) => {
    return (
      <Box className="grid grid-cols-1 gap-4">
        <Typography variant="h5" className="font-bold mb-4">
          Basic Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Controller
              name="firstName"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="First Name"
                  fullWidth
                  required
                  disabled={isDisabled}
                  variant="outlined"
                  error={!!errors.firstName}
                  helperText={errors.firstName ? "First name is required" : ""}
                />
              )}
              rules={{ required: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="middleName"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  disabled={isDisabled}
                  label="Middle Name"
                  fullWidth
                  variant="outlined"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="lastName"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Last Name"
                  fullWidth
                  required
                  disabled={isDisabled}
                  variant="outlined"
                  error={!!errors.lastName}
                  helperText={errors.lastName ? "Last name is required" : ""}
                />
              )}
              rules={{ required: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="preferredName"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  disabled={isDisabled}
                  label="Preferred Name"
                  fullWidth
                  variant="outlined"
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="dob"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Date of Birth"
                  type="date"
                  disabled={isDisabled}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  variant="outlined"
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="county"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={allCounties}
                  getOptionLabel={(option) => option || ""}
                  loading={loadingCounties}
                  onChange={(event, newValue) => {
                    setValue("county", newValue ? newValue : "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Home County"
                      fullWidth
                      required
                      disabled={isDisabled}
                      variant="outlined"
                      error={!!errors.county}
                      helperText={errors.county ? "County is required" : ""}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCounties ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
              rules={{ required: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography
              sx={{
                fontSize: "12px",
                color: "rgba(0, 0, 0, 0.6)",
                marginBottom: "4px",
                fontWeight: 500,
              }}
            >
              Search Address
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <GoogleAutoComplete
                placeholder="Search Address"
                callback={handleGoogleCallback}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="address"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  disabled={isDisabled}
                  {...field}
                  label="Street Address"
                  fullWidth
                  variant="outlined"
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="zipCode"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  disabled={isDisabled}
                  {...field}
                  label="ZIP/Postal Code"
                  fullWidth
                  variant="outlined"
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="city"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={allCities}
                  getOptionLabel={(option) => option}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="City"
                      fullWidth
                      disabled={isDisabled}
                      variant="outlined"
                      error={!!errors.city}
                      helperText={errors.city ? "City is required" : ""}
                    />
                  )}
                  onChange={(event, value) => field.onChange(value)}
                />
              )}
              rules={{ required: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="state"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={allStates}
                  getOptionLabel={(option) => option}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State"
                      disabled={isDisabled}
                      fullWidth
                      variant="outlined"
                      error={!!errors.state}
                      helperText={errors.state ? "State is required" : ""}
                    />
                  )}
                  onChange={(event, value) => field.onChange(value)}
                />
              )}
              rules={{ required: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="email"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  disabled={isDisabled}
                  fullWidth
                  variant="outlined"
                  error={!!errors.email}
                  helperText={errors.email ? "Email is required" : ""}
                />
              )}
              rules={{ required: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography>Will you prefer to e-sign the Indemnity Agreement?</Typography>
            <Controller
              name="is_esignatures"
              control={control}
              defaultValue="Yes"
              render={({ field }) => (
                <RadioGroup disabled={isDisabled} {...field} row>
                  <FormControlLabel
                    disabled={isDisabled}
                    value="Yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    disabled={isDisabled}
                    value="No"
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography>May we text you?</Typography>
            <Controller
              name="mayWeText"
              control={control}
              defaultValue="Yes"
              render={({ field }) => (
                <RadioGroup disabled={isDisabled} {...field} row>
                  <FormControlLabel
                    disabled={isDisabled}
                    value="Yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    disabled={isDisabled}
                    value="No"
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={9}>
            <Controller
              name="cell_phone"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <InputMask
                  mask="999-999-9999"
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isDisabled}
                >
                  {(inputProps) => (
                    <TextField
                      {...inputProps}
                      label="Cell Phone"
                      disabled={isDisabled}
                      fullWidth
                      variant="outlined"
                      placeholder="123-456-7890"
                    />
                  )}
                </InputMask>
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography>Are you a U.S. Citizen?</Typography>
            <Controller
              name="usCitizen"
              control={control}
              defaultValue="Yes"
              render={({ field }) => (
                <RadioGroup disabled={isDisabled} {...field} row>
                  <FormControlLabel
                    disabled={isDisabled}
                    value="Yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    disabled={isDisabled}
                    value="No"
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              )}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <Controller
              name="ssn"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Social Security Number"
                  disabled={isDisabled}
                  type={pii_access && showSSN ? "text" : "password"}
                  fullWidth
                  variant="outlined"
                  error={!!errors.ssn}
                  helperText={errors.ssn ? "SSN is required" : ""}
                  InputProps={{
                    endAdornment:
                      pii_access ? (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle ssn visibility"
                            onClick={() => setShowSSN((prev) => !prev)}
                            edge="end"
                          >
                            {showSSN ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                  }}
                />
              )}
              rules={{ required: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                border: "1px solid #d1d5db", // Light gray border
                borderRadius: "8px", // Rounded corners
                backgroundColor: "#f9fafb", // Light background
                padding: "16px", // Spacing inside the box
                margin: "16px 0", // Vertical spacing outside the box
              }}
            >
              <Typography variant="h6" sx={{ marginBottom: "2px" }}>
                Authorization to Obtain Underwriting Information
              </Typography>
              <Typography>
                Do you fully understand and agree, by submitting this request
                for approval you are providing authorization for Probate Bond
                Pros, surety companies and/or third-party brokers to verify
                information you provided as well as permission to obtain your
                consumer information from any source which would be necessary to
                determine bonding eligibility? This includes obtaining credit
                reports at time of application, during any review or renewal, at
                the time of any potential or actual claim, and/or for any other
                legitimate purposes determined by the surety in its reasonable
                discretion. This information will be held in the strictest
                confidence. Do you agree?
              </Typography>

              <Controller
                name="authorizationAgreement"
                control={control}
                defaultValue={true} // Default value set to true (Yes)
                rules={{ required: "You must select an option" }}
                render={({ field }) => (
                  <RadioGroup {...field} row>
                    <FormControlLabel
                      value={true}
                      control={<Radio />}
                      label="Yes"
                      disabled={isDisabled}
                    />
                    <FormControlLabel
                      value={false}
                      control={<Radio />}
                      label="No"
                      disabled={isDisabled}
                    />
                  </RadioGroup>
                )}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const DetailedInformation = ({ isDisabled }) => {
    return (
      <Box className="grid grid-cols-1 gap-4">
        <Typography variant="h5" className="font-bold mb-4">
          Detailed Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography>Have you been bankrupt or insolvent?</Typography>
            <Controller
              name="bankrupt"
              control={control}
              defaultValue="No"
              render={({ field }) => (
                <RadioGroup disabled={isDisabled} {...field} row>
                  <FormControlLabel
                    disabled={isDisabled}
                    value="Yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    disabled={isDisabled}
                    value="No"
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              )}
            />
          </Grid>

          {watch("bankrupt") === "Yes" && (
            <Grid item xs={12}>
              <Controller
                name="bankruptExplanation"
                control={control}
                defaultValue=""
                rules={{ required: "Explanation is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    disabled={isDisabled}
                    label="Provide an explanation (Year)"
                    fullWidth
                    variant="outlined"
                    error={!!errors.bankruptExplanation}
                    helperText={errors.bankruptExplanation?.message}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography>
              Any lawsuits, judgments, or liens against you?
            </Typography>
            <Controller
              name="lawsuits"
              control={control}
              defaultValue="No"
              render={({ field }) => (
                <RadioGroup disabled={isDisabled} {...field} row>
                  <FormControlLabel
                    disabled={isDisabled}
                    value="Yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    disabled={isDisabled}
                    value="No"
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              )}
            />
          </Grid>

          {watch("lawsuits") === "Yes" && (
            <Grid item xs={12}>
              <Controller
                name="lawsuitsExplanation"
                control={control}
                defaultValue=""
                rules={{ required: "Explanation is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    disabled={isDisabled}
                    label="Provide an explanation"
                    multiline
                    minRows={2}
                    maxRows={6}
                    fullWidth
                    variant="outlined"
                    error={!!errors.lawsuitsExplanation}
                    helperText={errors.lawsuitsExplanation?.message}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography>
              Has application for this bond been declined by another surety?
            </Typography>
            <Controller
              name="declinedSurety"
              control={control}
              defaultValue="No"
              render={({ field }) => (
                <RadioGroup disabled={isDisabled} {...field} row>
                  <FormControlLabel
                    disabled={isDisabled}
                    value="Yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    disabled={isDisabled}
                    value="No"
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              )}
            />
          </Grid>

          {watch("declinedSurety") === "Yes" && (
            <Grid item xs={12}>
              <Controller
                name="suretyExplanation"
                control={control}
                defaultValue=""
                rules={{ required: "Explanation is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Provide an explanation"
                    disabled={isDisabled}
                    multiline
                    minRows={2}
                    maxRows={6}
                    fullWidth
                    variant="outlined"
                    error={!!errors.suretyExplanation}
                    helperText={errors.suretyExplanation?.message}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography>Is this bond replacing a previous bond?</Typography>
            <Controller
              name="replacingBond"
              control={control}
              defaultValue="No"
              render={({ field }) => (
                <RadioGroup disabled={isDisabled} {...field} row>
                  <FormControlLabel
                    disabled={isDisabled}
                    value="Yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    disabled={isDisabled}
                    value="No"
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              )}
            />
          </Grid>

          {watch("replacingBond") === "Yes" && (
            <>
              <Grid item xs={12}>
                <Controller
                  name="bondReplacementReason"
                  control={control}
                  defaultValue=""
                  rules={{ required: "Explanation is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Provide surety name and reason for change"
                      disabled={isDisabled}
                    multiline
                    minRows={2}
                    maxRows={6}
                      fullWidth
                      variant="outlined"
                      error={!!errors.bondReplacementReason}
                      helperText={errors.bondReplacementReason?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="doe"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Date of Expiration of Current Bond"
                      disabled={isDisabled}
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Typography>Which bond term would you like?</Typography>
            <Controller
              name="bondTerm"
              control={control}
              defaultValue="1 Year Bond"
              render={({ field }) => (
                <RadioGroup disabled={isDisabled} {...field} row>
                  <FormControlLabel
                    value="1 Year Bond"
                    disabled={isDisabled}
                    control={<Radio />}
                    label={
                      <>
                        <Typography
                          component="span"
                          sx={{ fontWeight: "bold" }}
                        >
                          $175.00
                        </Typography>{" "}
                        (1 Year Bond)
                      </>
                    }
                  />
                  <FormControlLabel
                    value="2 Year Bond"
                    disabled={isDisabled}
                    control={<Radio />}
                    label={
                      <>
                        <Typography
                          component="span"
                          sx={{ fontWeight: "bold" }}
                        >
                          $306.00
                        </Typography>{" "}
                        (2 Year Bond)
                      </>
                    }
                  />
                  <FormControlLabel
                    value="3 Year Bond"
                    disabled={isDisabled}
                    control={<Radio />}
                    label={
                      <>
                        <Typography
                          component="span"
                          sx={{ fontWeight: "bold" }}
                        >
                          $437.00
                        </Typography>{" "}
                        (3 Year Bond)
                      </>
                    }
                  />
                </RadioGroup>
              )}
            />
          </Grid>
        </Grid>
        {/* Heading */}
        <Typography variant="h5" className="font-bold mb-4">
          Documents
        </Typography>

        <Box className="grid grid-cols-1 gap-6">
          {/* Bond Application Document */}
          <Box>
            <Typography variant="body1" className="mb-2">
              Bond Application
            </Typography>
            {watch("bondAppFileName") &&
            watch("bondAppFileName") !== "No file uploaded" ? (
              <Button
                variant="outlined"
                color="secondary"
                href={watch("bondAppLink")}
                target="_blank"
                rel="noopener noreferrer"
                fullWidth
                sx={{ mt: 1 }}
              >
                View Document
              </Button>
            ) : (
              <Typography variant="body2" color="textSecondary" mt={1}>
                No document available
              </Typography>
            )}

            <Typography variant="body2" color="textSecondary" mt={1}>
              {watch("bondAppFileName") &&
                watch("bondAppFileName") !== "No file uploaded" &&
                `File name: ${watch("bondAppFileName")}`}
            </Typography>
          </Box>

          {/* Bond Document */}
          <Box>
            <Typography variant="body1" className="mb-2">
              Bond Document
            </Typography>
            {watch("bondFileName") &&
            watch("bondFileName") !== "No file uploaded" ? (
              <Button
                variant="outlined"
                color="secondary"
                href={watch("bondLink")}
                target="_blank"
                rel="noopener noreferrer"
                fullWidth
                sx={{ mt: 1 }}
              >
                View Document
              </Button>
            ) : (
              <Typography variant="body2" color="textSecondary" mt={1}>
                No document available
              </Typography>
            )}

            <Typography variant="body2" color="textSecondary" mt={1}>
              {watch("bondFileName") &&
                watch("bondFileName") !== "No file uploaded" &&
                `File name: ${watch("bondFileName")}`}
            </Typography>
          </Box>

          {/* Indemnity Agreement */}
          <Box>
            <Typography variant="body1" className="mb-2">
              Indemnity Agreement
            </Typography>
            {watch("indemnityFileName") &&
            watch("indemnityFileName") !== "No file uploaded" ? (
              <Button
                variant="outlined"
                color="secondary"
                href={watch("indemnityLink")}
                target="_blank"
                rel="noopener noreferrer"
                fullWidth
                sx={{ mt: 1 }}
              >
                View Document
              </Button>
            ) : (
              <Typography variant="body2" color="textSecondary" mt={1}>
                No document available
              </Typography>
            )}

            <Typography variant="body2" color="textSecondary" mt={1}>
              {watch("indemnityFileName") &&
                watch("indemnityFileName") !== "No file uploaded" &&
                `File name: ${watch("indemnityFileName")}`}
            </Typography>
          </Box>

          {/* Cancellation Document */}
          <Box>
            <Typography variant="body1" className="mb-2">
              Cancellation Document
            </Typography>
            {watch("cancellationDocument") &&
            watch("cancellationDocument") !== "No file uploaded" ? (
              <Button
                variant="outlined"
                color="secondary"
                href={watch("cancellationDocumentLink")}
                target="_blank"
                rel="noopener noreferrer"
                fullWidth
                sx={{ mt: 1 }}
              >
                View Document
              </Button>
            ) : (
              <Typography variant="body2" color="textSecondary" mt={1}>
                No document available
              </Typography>
            )}

            <Typography variant="body2" color="textSecondary" mt={1}>
              {watch("cancellationDocument") &&
                watch("cancellationDocument") !== "No file uploaded" &&
                `File name: ${watch("cancellationDocument")}`}
            </Typography>
          </Box>

          {/* Credit Report Document */}
          {(role === "carrier_team_member" || (role === "super_user" && pii_access) || (role === "internal_user" && pii_access)) && (
            <Box>
              <Typography variant="body1" className="mb-2">
                Credit Report Document
              </Typography>
              {watch("creditDocument") &&
              watch("creditDocument") !== "No file uploaded" ? (
                <Button
                  variant="outlined"
                  color="secondary"
                  href={watch("creditDocumentLink")}
                  target="_blank"
                  rel="noopener noreferrer"
                  fullWidth
                  sx={{ mt: 1 }}
                >
                  View Document
                </Button>
              ) : (
                <Typography variant="body2" color="textSecondary" mt={1}>
                  No document available
                </Typography>
              )}

              <Typography variant="body2" color="textSecondary" mt={1}>
                {watch("creditDocument") &&
                  watch("creditDocument") !== "No file uploaded" &&
                  `File name: ${watch("creditDocument")}`}
              </Typography>
            </Box>
          )}

          {/* FCRA Document */}
          <Box>
            <Typography variant="body1" className="mb-2">
              FCRA Document
            </Typography>
            {watch("fcraDocument") &&
            watch("fcraDocument") !== "No file uploaded" ? (
              <Button
                variant="outlined"
                color="secondary"
                href={watch("fcraDocumentLink")}
                target="_blank"
                rel="noopener noreferrer"
                fullWidth
                sx={{ mt: 1 }}
              >
                View Document
              </Button>
            ) : (
              <Typography variant="body2" color="textSecondary" mt={1}>
                No document available
              </Typography>
            )}

            <Typography variant="body2" color="textSecondary" mt={1}>
              {watch("fcraDocument") &&
                watch("fcraDocument") !== "No file uploaded" &&
                `File name: ${watch("fcraDocument")}`}
            </Typography>
          </Box>

          {/* Verification Certificate */}
          <Box>
            <Typography variant="body1" className="mb-2">
              Verification Certificate
            </Typography>
            {watch("verificationCertDocument") &&
            watch("verificationCertDocument") !== "" ? (
              <Button
                variant="outlined"
                color="secondary"
                href={watch("verificationCertDocumentLink")}
                target="_blank"
                rel="noopener noreferrer"
                fullWidth
                sx={{ mt: 1 }}
              >
                View Document
              </Button>
            ) : (
              <Typography variant="body2" color="textSecondary" mt={1}>
                No document available
              </Typography>
            )}

            <Typography variant="body2" color="textSecondary" mt={1}>
              {watch("verificationCertDocument") &&
                watch("verificationCertDocument") !== "" &&
                `File name: ${watch("verificationCertDocument")}`}
            </Typography>
          </Box>

          {/* Verification Power of Attorney */}
          <Box>
            <Typography variant="body1" className="mb-2">
              Power of Attorney
            </Typography>
            {watch("verificationPoaDocument") &&
            watch("verificationPoaDocument") !== "" ? (
              <Button
                variant="outlined"
                color="secondary"
                href={watch("verificationPoaDocumentLink")}
                target="_blank"
                rel="noopener noreferrer"
                fullWidth
                sx={{ mt: 1 }}
              >
                View Document
              </Button>
            ) : (
              <Typography variant="body2" color="textSecondary" mt={1}>
                No document available
              </Typography>
            )}

            <Typography variant="body2" color="textSecondary" mt={1}>
              {watch("verificationPoaDocument") &&
                watch("verificationPoaDocument") !== "" &&
                `File name: ${watch("verificationPoaDocument")}`}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const ObligeeInformation = ({ isDisabled }) => (
    <Box className="grid grid-cols-1 gap-4">
      <Typography variant="h5" className="font-bold mb-4">
        Obligee Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Controller
            name="obligeeId"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={obligees}
                getOptionLabel={(option) => option.obligee_name || ""}
                loading={loadingObligees}
                value={
                  obligees.find((o) => o.obligee_id === field.value) || null
                }
                onChange={(event, newValue) => {
                  field.onChange(newValue ? newValue.obligee_id : null);
                }}
                disabled={isDisabled}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Obligee"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingObligees ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          />
        </Grid>

        {!isDisabled && (
          <Grid item xs={12}>
            <Button
              variant="outlined"
              onClick={() => setOpenObligeeDialog(true)}
              disabled={loadingObligees}
              fullWidth
            >
              {loadingObligees ? (
                <CircularProgress size={24} />
              ) : (
                "Add New Obligee"
              )}
            </Button>
          </Grid>
        )}

        {watch("obligeeId") && (
          <Grid item xs={12}>
            <Box
              sx={{
                mt: 2,
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                backgroundColor: "#f9f9f9",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                Selected Obligee:
              </Typography>
              {(() => {
                const selected = obligees.find(
                  (o) => o.obligee_id === watch("obligeeId")
                );
                return selected ? (
                  <>
                    <Typography sx={{ mb: 1 }}>
                      <Box component="span" sx={{ fontWeight: "bold" }}>
                        Name:
                      </Box>{" "}
                      {selected.obligee_name}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <Box component="span" sx={{ fontWeight: "bold" }}>
                        Address:
                      </Box>{" "}
                      {selected.obligee_address}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <Box component="span" sx={{ fontWeight: "bold" }}>
                        City:
                      </Box>{" "}
                      {selected.obligee_city}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <Box component="span" sx={{ fontWeight: "bold" }}>
                        State:
                      </Box>{" "}
                      {selected.obligee_state}
                    </Typography>
                    <Typography>
                      <Box component="span" sx={{ fontWeight: "bold" }}>
                        ZIP:
                      </Box>{" "}
                      {selected.obligee_zip}
                    </Typography>
                  </>
                ) : null;
              })()}
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Add New Obligee Dialog */}
      <Dialog
        open={openObligeeDialog}
        onClose={() => setOpenObligeeDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New Obligee</DialogTitle>
        <DialogContent>
          <TextField
            label="Obligee Name"
            value={newObligee.name}
            onChange={(e) =>
              setNewObligee({ ...newObligee, name: e.target.value })
            }
            fullWidth
            margin="normal"
            required
            sx={{ mb: 2 }}
          />

          <GoogleAutoComplete
            placeholder="Search Address"
            callback={handleObligeeGoogleCallback}
          />

          <TextField
            label="Street Address"
            value={newObligee.street}
            onChange={(e) =>
              setNewObligee({ ...newObligee, street: e.target.value })
            }
            fullWidth
            margin="normal"
            required
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Autocomplete
              options={allCities}
              value={newObligee.city}
              onChange={(e, value) =>
                setNewObligee({ ...newObligee, city: value })
              }
              renderInput={(params) => (
                <TextField {...params} label="City" fullWidth required />
              )}
              sx={{ flex: 1 }}
            />

            <Autocomplete
              options={allStates}
              value={newObligee.state}
              onChange={(e, value) =>
                setNewObligee({ ...newObligee, state: value })
              }
              renderInput={(params) => (
                <TextField {...params} label="State" fullWidth required />
              )}
              sx={{ flex: 1 }}
            />
          </Box>

          <TextField
            label="ZIP Code"
            value={newObligee.zip}
            onChange={(e) =>
              setNewObligee({ ...newObligee, zip: e.target.value })
            }
            fullWidth
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenObligeeDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddObligee}
            variant="contained"
            disabled={
              !newObligee.name ||
              !newObligee.street ||
              !newObligee.city ||
              !newObligee.state ||
              !newObligee.zip
            }
          >
            Add Obligee
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  return (
    <>
      <Navbar />
      <Container maxWidth="md" sx={{ mt: 6, mb: 6 }}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ fontWeight: "bold" }}
        >
          Guardianship Blanket Bond Request
        </Typography>

        {data?.underwriting_status &&
          data.underwriting_status?.trim()?.toLowerCase() !== "not required" &&
          (role === "super_user" || role === "internal_user") && (
            <Box
              sx={{
                width: "100%",
                mb: 2,
                p: 2,
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
                border: "1px solid #e0e0e0",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  color: ["approved", "overrided"].includes(data.underwriting_status?.trim()?.toLowerCase())
                    ? "success.main"
                    : ["pending", "rejected"].includes(data.underwriting_status?.trim()?.toLowerCase())
                      ? "error.main"
                      : "warning.main",
                  mt: 0.25,
                }}
              >
                {["approved", "overrided"].includes(data.underwriting_status?.trim()?.toLowerCase()) ? (
                  <CheckCircleIcon fontSize="small" />
                ) : (
                  <InfoOutlinedIcon fontSize="small" />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                  Underwriting
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  <div>
                    <span style={{ fontWeight: 700 }}>Status:</span>{" "}
                    <span style={{ fontWeight: 400, color: "inherit" }}>
                      {data.underwriting_status?.trim()?.toLowerCase() === "overrided"
                        ? (data.overrided_by?.trim() || data.overrided_at)
                            ? `Not Required (Overrided by ${(data.overrided_by || "").trim()} at ${data.overrided_at ? new Date(data.overrided_at).toLocaleString() : ""})`
                            : "Not Required"
                        : (() => { const s = data.underwriting_status?.trim()?.toLowerCase(); return s ? s.charAt(0).toUpperCase() + s.slice(1) : ""; })()}
                    </span>
                  </div>
                  {data.underwriting_status?.trim()?.toLowerCase() === "overrided" &&
                    (data.override_reason || "").trim() && (
                      <div><span style={{ fontWeight: 700 }}>Override reason:</span>{" "}<span style={{ fontWeight: 400 }}>{(data.override_reason || "").trim()}</span></div>
                    )}
                  {data.underwriting_status?.trim()?.toLowerCase() !== "overrided" &&
                    data.underwriting_status?.trim()?.toLowerCase() !== "not required" &&
                    data.underwriting_reason &&
                    data.underwriting_reason !== "N/A" && (
                      <div>Reason for Trigger: {data.underwriting_reason}</div>
                    )}
                </Typography>
              </Box>
            </Box>
          )}

        {pgData !== null &&
          pgData.ams_customer_number === null &&
          !disabled &&
          (role === "super_user" || role === "internal_user") && (
            <Box
              sx={{
                width: "100%",
                mb: 2,
                p: 2,
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
                border: "1px solid #e0e0e0",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <Box sx={{ color: "warning.main", mt: 0.25 }}>
                <WarningAmberIcon fontSize="small" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                  AMS customer number
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  The AMS Customer Number for this Professional Guardian is not set. System will automatically create a new customer with a customer number in AMS.
                </Typography>
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      navigate(`/view_professional_guardian/${pgId}`);
                    }}
                  >
                    Enter Now
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Basic Information Section */}
          <Paper
            elevation={3}
            sx={{ p: 4, backgroundColor: "#f5f5f5", borderRadius: "15px" }}
          >
            <BasicInformation isDisabled={disabled} />
          </Paper>

          {/* Vertical Space */}
          <Box sx={{ mt: 4 }} />

          {/* Detailed Information Section */}
          <Paper
            elevation={3}
            sx={{ p: 4, backgroundColor: "#f5f5f5", borderRadius: "15px" }}
          >
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <DetailedInformation isDisabled={disabled} role={role} pii_access={pii_access} />
            </Box>
          </Paper>

          {/* Vertical Space */}
          <Box sx={{ mt: 4 }} />

          {/* Obligee Information Section */}
          <Paper
            elevation={3}
            sx={{ p: 4, backgroundColor: "#f5f5f5", borderRadius: "15px" }}
          >
            <ObligeeInformation isDisabled={disabled} />
          </Paper>

          {!disabled && (
            <BondNumberSelect data={bondNum} updateData={updateBondNum} />
          )}

          {!disabled && (
            <Box
              sx={{
                maxWidth: 765,
                mx: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                padding: 3,
                mt: 3,
                mb: 3,
                boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                backgroundColor: "#f3f4f6", // bg-gray-100
              }}
            >
              <Typography variant="h5" className="font-bold mb-4">
                Billing Method
              </Typography>

              <RadioGroup
                row
                value={billingMethod}
                onChange={(e) => setBillingMethod(e.target.value)}
              >
                <FormControlLabel
                  value="Direct Bill"
                  control={<Radio />}
                  label="Direct Bill"
                  sx={{ mr: 4 }}
                />
                <FormControlLabel
                  value="Agency Bill"
                  control={<Radio />}
                  label="Agency Bill"
                />
              </RadioGroup>
            </Box>
          )}

          {/* Additional Information */}
          {(role === "super_user" || role === "internal_user") && (
            <Box
              sx={{
                maxWidth: 765,
                mx: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                padding: 3,
                mt: 3,
                mb: 3,
                boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                backgroundColor: "#f3f4f6", // bg-gray-100
              }}
            >
              <Typography variant="h5" className="font-bold mb-4">
                Additional Information
              </Typography>

              <TextField
                select
                label="Is Signed Indemnity Received"
                value={isIndemnityReceived}
                onChange={(e) => setIsIndemnityReceived(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              >
                <MenuItem value="">Not Selected</MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </TextField>
              <TextField
                fullWidth
                label="Created At"
                type="datetime-local"
                value={editableCreatedAt}
                onChange={(e) => setEditableCreatedAt(e.target.value)}
                sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
                helperText={data?.bond?.created_at || data?.created_at ? `Current: ${new Date(data?.created_at ?? data?.bond?.created_at).toLocaleString()}` : ""}
              />
            </Box>
          )}

          {/* Buttons */}
          {!disabled && (
            <Box
              sx={{ display: "flex", justifyContent: "center", mt: 4, gap: 2 }}
            >
              <Button
                endIcon={
                  updateLoading && (
                    <CircularProgress size={20} color="inherit" />
                  )
                }
                type="submit"
                variant="contained"
                color="primary"
              >
                Update
              </Button>

              <Button
                variant="contained"
                sx={{ backgroundColor: "#4caf50", color: "white" }}
                onClick={handleMenuClick}
                disabled={approveLoading}
                endIcon={
                  approveLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <ArrowDropDownIcon />
                  )
                }
              >
                {approveLoading ? "Approving..." : "Approve"}
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem
                  onClick={() => {
                    if (!billingMethod) {
                      toast.error(
                        "Please select a Billing Method before approving with invoice."
                      );
                      handleMenuClose();
                      return;
                    }
                    handleApproveOption({
                      approval: "approval",
                      billing_method: billingMethod,
                      ams_flow: true,
                    });
                  }}
                >
                  Approve with Policy
                </MenuItem>

                <MenuItem
                  onClick={() =>
                    handleApproveOption({
                      billing_method: "Agency Bill",
                      ams_flow: false,
                    })
                  }
                >
                  Approve without Policy
                </MenuItem>
              </Menu>
              <Button
                endIcon={
                  rejectLoading && (
                    <CircularProgress size={20} color="inherit" />
                  )
                }
                type="button"
                variant="contained"
                sx={{ backgroundColor: "#f44336", color: "white" }}
                onClick={() => setRejectDialogOpen(true)}
              >
                Reject
              </Button>

              {/* Underwriting Dropdown */}
              <Button
                variant="contained"
                color="warning"
                onClick={handleUnderwritingMenuClick}
                disabled={isUnderwritingLoading}
                endIcon={
                  isUnderwritingLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <ArrowDropDownIcon />
                  )
                }
              >
                {isUnderwritingLoading ? "Processing..." : "Underwriting"}
              </Button>

              <Menu
                anchorEl={underwritingAnchorEl}
                open={Boolean(underwritingAnchorEl)}
                onClose={handleUnderwritingMenuClose}
              >
                <MenuItem onClick={handleManualUnderwritingClick}>
                  Trigger Underwriting
                </MenuItem>
                <MenuItem onClick={handleOverrideUnderwritingClick}>
                  Override Underwriting
                </MenuItem>
                <MenuItem onClick={() => setIsCreditDialogOpen(true)}>
                  Upload Credit Report
                </MenuItem>
              </Menu>

              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#9c27b0",
                  color: "white",
                  "&:hover": { backgroundColor: "#7b1fa2" },
                }}
                onClick={(event) => setRenewalsAnchorEl(event.currentTarget)}
                disabled={verificationLoading}
                endIcon={
                  verificationLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <ArrowDropDownIcon />
                  )
                }
              >
                {verificationLoading ? "Processing Renewal..." : "Renewals"}
              </Button>

              <Menu
                anchorEl={renewalsAnchorEl}
                open={Boolean(renewalsAnchorEl)}
                onClose={() => setRenewalsAnchorEl(null)}
              >
                <MenuItem onClick={() => handleVerificationCertificate()}>
                  Verification Certificate
                </MenuItem>
              </Menu>

              {/* Verification Certificate – edit effective & expiration dates */}
              <Dialog
                open={verificationCertDialogOpen}
                onClose={() => {
                  if (!verificationLoading) {
                    setVerificationCertDialogOpen(false);
                    setVerificationCertFormData(null);
                  }
                }}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Verification Certificate</DialogTitle>
                <DialogContent dividers sx={{ pt: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Edit the bond term and effective/expiration dates to be sent in the payload. If you change the bond term, the bond will be updated first, then the verification certificate will be sent.
                  </Typography>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Bond Term</Typography>
                  <RadioGroup
                    row
                    value={verificationCertBondTerm}
                    onChange={(e) => setVerificationCertBondTerm(e.target.value)}
                    sx={{ mb: 2 }}
                  >
                    {BOND_TERM_OPTIONS.map((term) => (
                      <FormControlLabel
                        key={term}
                        value={term}
                        control={<Radio size="small" />}
                        label={term}
                      />
                    ))}
                  </RadioGroup>
                  <TextField
                    label="Effective Date"
                    type="date"
                    value={verificationCertEffectiveDate}
                    onChange={(e) => setVerificationCertEffectiveDate(e.target.value)}
                    fullWidth
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Expiration Date"
                    type="date"
                    value={verificationCertExpirationDate}
                    onChange={(e) => setVerificationCertExpirationDate(e.target.value)}
                    fullWidth
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => {
                      setVerificationCertDialogOpen(false);
                      setVerificationCertFormData(null);
                    }}
                    disabled={verificationLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleVerificationCertConfirm}
                    variant="contained"
                    color="primary"
                    disabled={verificationLoading || !verificationCertEffectiveDate || !verificationCertExpirationDate}
                  >
                    Confirm
                  </Button>
                </DialogActions>
              </Dialog>

              <Button
                variant="contained"
                color="info"
                onClick={handleSignIndemnity}
                disabled={isEsignatureLoading || data?.bond?.status === "Active" || data?.bond?.third_document_url === null || watch("is_esignatures") === "No"}
                endIcon={isEsignatureLoading && <CircularProgress size={20} />}
              >
                {isEsignatureLoading ? "Signing..." : "Trigger Formstack"}
              </Button>
            </Box>
          )}

          {role === "carrier_team_member" && (
            <Box
              className="flex justify-center"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                mt: 2,
              }}
            >
              <Button
                variant="contained"
                color="success"
                onClick={() => handleUpdateUnderwriting("approved")}
                disabled={isApproveLoading || !data?.is_underwriting}
                endIcon={isApproveLoading && <CircularProgress size={20} />}
              >
                {isApproveLoading ? "Approving..." : "Approve"}
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={() => handleUpdateUnderwriting("rejected")}
                disabled={isRejectLoading || !data?.is_underwriting}
                endIcon={isRejectLoading && <CircularProgress size={20} />}
              >
                {isRejectLoading ? "Rejecting..." : "Reject"}
              </Button>
            </Box>
          )}

          {(role === "carrier_team_member" ||
            role === "super_user" ||
            role === "internal_user") && (
            <Box sx={{ position: "fixed", bottom: 16, right: 16, zIndex: 1500 }}>
              <Badge
                overlap="circular"
                color="success"
                invisible={!chatUnreadTotal}
                badgeContent={chatUnreadTotal}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{ '& .MuiBadge-badge': { top: -6, right: -6, zIndex: 2, transform: 'none', minWidth: 18, height: 18, fontSize: 11, fontWeight: 600, boxShadow: 1 } }}
              >
                <Fab
                  aria-label="chat"
                  sx={{ background: "linear-gradient(135deg, #111 0%, #333 100%)", color: "#fff", border: "1px solid #444", boxShadow: 6, "&:hover": { background: "linear-gradient(135deg, #1a1a1a 0%, #4d4d4d 100%)" } }}
                  onClick={() => setOpenChat(!openChat)}
                >
                  {openChat ? (
                    <CloseIcon sx={{ color: "#fff" }} />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.2,
                      }}
                    >
                      <ChatIcon sx={{ color: "#fff" }} />
                      <Typography variant="body1" sx={{ fontSize: 8, color: "#fff" }}>
                        UW
                      </Typography>
                    </Box>
                  )}
                </Fab>
              </Badge>
            </Box>
          )}
          {/* Render ChatWindow if openChat is true */}
          {openChat &&
            (role === "carrier_team_member" ||
              role === "super_user" ||
              role === "internal_user") && (
              <ChatWindow
                username={username}
                bondId={bond_id}
                role={role}
                onClose={() => setOpenChat(false)}
              />
            )}
        </form>
      </Container>
      <Dialog
        open={rejectDialogOpen}
        onClose={() => {
          if (!rejectFcraUploading) {
            setRejectDialogOpen(false);
            setRejectFcraFile(null);
          }
        }}
      >
        <DialogTitle>Reject Bond</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You may optionally attach an FCRA document before rejecting. You can also reject without uploading.
          </Typography>
          <Button
            variant="outlined"
            component="label"
            disabled={rejectFcraUploading}
            startIcon={<CloudUploadIcon />}
            sx={{
              borderRadius: 2,
              borderColor: "rgba(37, 99, 235, 0.5)",
              color: "#2563eb",
              fontWeight: 600,
              textTransform: "uppercase",
              mb: 1,
              "&:hover": {
                borderColor: "#2563eb",
                backgroundColor: "rgba(37, 99, 235, 0.04)",
              },
            }}
          >
            Choose File
            <input
              type="file"
              hidden
              accept="application/pdf,.pdf,image/*"
              onChange={(e) => setRejectFcraFile(e.target.files?.[0] || null)}
              disabled={rejectFcraUploading}
            />
          </Button>
          {rejectFcraFile && (
            <Typography variant="body2" sx={{ display: "block", mt: 1, color: "text.primary" }}>
              Selected File: {rejectFcraFile.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectDialogOpen(false);
              setRejectFcraFile(null);
            }}
            color="secondary"
            disabled={rejectFcraUploading}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleReject()}
            disabled={rejectLoading}
            sx={{ color: "#f44336", borderColor: "#f44336" }}
          >
            Reject without FCRA
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#f44336", color: "white" }}
            disabled={rejectLoading || rejectFcraUploading}
            onClick={async () => {
              if (rejectFcraFile) {
                setRejectFcraUploading(true);
                try {
                  const link = await uploadToADLS(rejectFcraFile);
                  await handleReject({
                    fcra_document: rejectFcraFile.name,
                    fcra_document_link: link,
                  });
                } catch (err) {
                  console.error(err);
                  toast.error("Upload failed. Rejecting without FCRA document.");
                  await handleReject();
                } finally {
                  setRejectFcraUploading(false);
                }
              } else {
                await handleReject();
              }
            }}
            endIcon={rejectFcraUploading && <CircularProgress size={20} color="inherit" />}
          >
            {rejectFcraUploading ? "Uploading..." : "Upload & Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isCreditDialogOpen}
        onClose={() => setIsCreditDialogOpen(false)}
      >
        <DialogTitle>Upload Credit Report</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Upload the credit report PDF to notify the underwriter.
          </Typography>
          <Box
            sx={{
              border: "1px solid #ccc",
              padding: "6px 12px",
              borderRadius: "4px",
              backgroundColor: "#fafafa",
              display: "inline-block",
              fontSize: "0.9rem",
              cursor: isCreditUploading ? "not-allowed" : "pointer",
            }}
          >
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setCreditReportFile(e.target.files[0])}
              disabled={isCreditUploading}
              style={{ fontSize: "0.85rem", width: "100%" }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsCreditDialogOpen(false)}
            color="secondary"
            disabled={isCreditUploading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              if (!creditReportFile) {
                toast.error("Please upload a PDF file.");
                return;
              }
              setIsCreditUploading(true);
              try {
                await runCreditReport(bond_id, creditReportFile);
                toast.success("Credit report uploaded successfully.");
                setIsCreditDialogOpen(false);
                setCreditReportFile(null);
              } catch (error) {
                console.error(error);
                toast.error("Upload failed. Try again.");
              } finally {
                setIsCreditUploading(false);
              }
            }}
            disabled={isCreditUploading}
            endIcon={isCreditUploading && <CircularProgress size={20} />}
          >
            {isCreditUploading ? "Uploading..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual underwriting creation dialog */}
      <Dialog open={manualUnderwritingDialogOpen} onClose={() => setManualUnderwritingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Trigger Underwriting</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason"
            placeholder="Enter reason for creating underwriting entry"
            value={manualUnderwritingReason}
            onChange={(e) => setManualUnderwritingReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
            error={!(manualUnderwritingReason || "").trim()}
            helperText={!(manualUnderwritingReason || "").trim() ? "Reason is required" : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setManualUnderwritingDialogOpen(false); setManualUnderwritingReason(""); }}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSubmitManualUnderwriting} disabled={isUnderwritingLoading || !(manualUnderwritingReason || "").trim()}>
            {isUnderwritingLoading ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Override underwriting reason dialog */}
      <Dialog open={overrideReasonDialogOpen} onClose={() => setOverrideReasonDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Override Underwriting</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for override"
            placeholder="Enter reason for overriding underwriting"
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
            error={!(overrideReason || "").trim()}
            helperText={!(overrideReason || "").trim() ? "Reason is required" : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOverrideReasonDialogOpen(false); setOverrideReason(""); }}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleSubmitOverrideReason} disabled={isUnderwritingLoading || !(overrideReason || "").trim()}>
            {isUnderwritingLoading ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default VermostBlanketBondRetrieveForm;
